import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EventIcon from "@mui/icons-material/Event";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { OverAllLoader } from "../../components/OverAllLoader";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { FeedbackOutlined } from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";
import RecommendIcon from "@mui/icons-material/Recommend";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { useSnackbar } from "providers/SnackbarProvider";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

// Shared styles
const iconStyle = {
    color: "#1c97e3",
    fontSize: "20px",
};

const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mt: 0.5,
};

const halfWidth = {
    width: "50%",
};

const sectionTitleStyle = {
    display: "flex",
    alignItems: "center",
    gap: 1,
};

const sectionIconStyle = {
    color: "#1c97e3",
    fontSize: "30px",
};

const sectionTextStyle = {
    fontSize: "20px",
    fontWeight: "bold",
};

const DetailRow = ({ icon: Icon, value }) => (
    <Grid sx={rowStyle}>
        <Icon sx={iconStyle} />
        <Typography>{value || "-"}</Typography>
    </Grid>
);

const Field = ({ label, value }) => (
    <Grid item xs={6} sx={halfWidth}>
        <Typography fontWeight="600">{label || "-"}</Typography>
        <Typography>{value || "-"}</Typography>
    </Grid>
);

const ViewInterviewScheduler = () => {
    const [overAllLoading, setOverAllLoading] = useState(false);
    const [interviewData, setInterviewData] = useState({});

    const { showSnackbar } = useSnackbar();

    const changeableItems = useChangeableItems();
    const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

    const location = useLocation();
    const { interviewId } = location.state || {};
    const navigate = useNavigate();

    const handleNavigate = () => {
        if (interviewData?.interviewer_response_status?.toLowerCase() === 'rejected') {
            showSnackbar("error", "Cannot edit rejected interviews. Please schedule a new interview.")
            return;
        }
        navigate("/add-interview-scheduler", {
            state: {
                interviewId: interviewData?.interview_id,
            },
        });
    };

    const fetchById = async () => {
        setOverAllLoading(true);
        try {
            const res = await api.post("ViewInterviewByID", {
                interview_id: interviewId,
            });
            setInterviewData(res?.data?.data || {});
        } catch (error) {
            console.error("Error fetching interview details:", error);
        } finally {
            setOverAllLoading(false);
        }
    };

    useEffect(() => {
        if (interviewId) {
            fetchById();
        }
    }, [interviewId]);

    if (overAllLoading) {
        return <OverAllLoader message="Fetching interview details..." />;
    }

    const candidate = interviewData?.candidate || {};
    const interviewer = interviewData?.interviewer || {};

    return (
        <Box sx={{ minHeight: "100vh", p: 1 }}>
            {/* ================= BACK BUTTON ================= */}
            <Grid
                sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "#1c97e3",
                    gap: "4px",
                    cursor: "pointer",
                    width: "fit-content",
                    mb: 2,
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        window.history.back();
                    }
                }}
                onClick={() => window.history.back()}
            >
                <ArrowBackIcon sx={{ fontSize: "17px" }} />
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#1c97e3",
                    }}
                >
                    Back
                </Typography>
            </Grid>

            {/* ================= VIEW CARD ================= */}
            <Box>
                {/* ================= HEADER ================= */}
                <Grid
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #d1d1d1",
                    }}
                >
                    <Grid
                        sx={{
                            mb: 2,
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        <Grid
                            sx={{
                                backgroundColor: "#1c97e3",
                                p: 0.5,
                                borderRadius: "10px",
                            }}
                        >
                            <EventIcon
                                sx={{
                                    color: "white",
                                    fontSize: "35px",
                                }}
                            />
                        </Grid>
                        <Typography
                            sx={{
                                fontSize: "30px",
                                fontWeight: "bold",
                            }}
                        >
                            Interview Details
                        </Typography>
                    </Grid>
                    <Grid>
                        <Button
                            variant="contained"
                            className="gradient-button"
                            startIcon={<EditIcon />}
                            onClick={handleNavigate}
                        >
                            Edit
                        </Button>
                    </Grid>
                </Grid>

                {/* ================= INTERVIEW DETAILS ================= */}
                <Box
                    sx={{
                        mt: -1,
                        display: "flex",
                        flexDirection: "column",
                        rowGap: 3,
                        p: 3,
                    }}
                >
                    {/* ================= PARTICIPANTS ================= */}
                    <Grid sx={sectionTitleStyle}>
                        <GroupIcon sx={sectionIconStyle} />
                        <Typography sx={sectionTextStyle}>
                            Participants
                        </Typography>
                    </Grid>

                    <Grid
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            rowGap: 2,
                            width: "100%",
                        }}
                    >
                        {/* Candidate + Interviewer */}
                        <Grid
                            sx={{
                                display: "flex",
                                mt: -1,
                            }}
                        >
                            {/* Candidate */}
                            <Grid item xs={6} sx={halfWidth}>
                                <Typography fontWeight="600">
                                    {`${getLabel('STUDENT')} Details`}
                                </Typography>
                                <DetailRow
                                    icon={PersonOutlineIcon}
                                    value={`${candidate.first_name || ""} ${candidate.last_name || ""}`.trim()}
                                />
                                <DetailRow
                                    icon={MailOutlineIcon}
                                    value={candidate.email}
                                />
                                <DetailRow
                                    icon={PhoneOutlinedIcon}
                                    value={candidate.phone}
                                />
                            </Grid>

                            {/* Interviewer */}
                            <Grid item xs={6} sx={halfWidth}>
                                <Typography fontWeight="600">
                                    {`${getLabel('INTERVIEWER')} Details`}
                                </Typography>
                                <DetailRow
                                    icon={PersonOutlineIcon}
                                    value={`${interviewer.first_name || ""} ${interviewer.last_name || ""}`.trim()}
                                />
                                <DetailRow
                                    icon={MailOutlineIcon}
                                    value={interviewer.email}
                                />
                                <DetailRow
                                    icon={PhoneOutlinedIcon}
                                    value={interviewer.phone}
                                />
                            </Grid>
                        </Grid>

                        {/* ================= PROGRAM DETAILS ================= */}
                        <Grid
                            sx={{
                                ...sectionTitleStyle,
                                mt: 1,
                            }}
                        >
                            <AssignmentOutlinedIcon sx={sectionIconStyle} />
                            <Typography sx={sectionTextStyle}>
                                Program Details
                            </Typography>
                        </Grid>

                        {/* Job Role + Drive */}
                        <Grid sx={{ display: "flex" }}>
                            <Field
                                label={`${getLabel('PROGRAMS')}`}
                                value={interviewData?.program_name}
                            />
                            <Field
                                label="Drive"
                                value={interviewData?.batch_name}
                            />
                        </Grid>

                        {/* Job Description */}
                        <Grid sx={{ display: "flex" }}>
                            <Field
                                label="Job Description"
                                value={interviewData?.program_outcome}
                            />
                        </Grid>
                    </Grid>

                    {/* ================= INTERVIEW SCHEDULE ================= */}
                    <Grid sx={sectionTitleStyle}>
                        <EventNoteOutlinedIcon sx={sectionIconStyle} />
                        <Typography sx={sectionTextStyle}>
                            Interview Schedule
                        </Typography>
                    </Grid>

                    {/* Scheduled Date + Time */}
                    <Grid
                        sx={{
                            display: "flex",
                            mt: -1,
                        }}
                    >
                        <Field
                            label="Scheduled Date"
                            value={
                                interviewData?.scheduled_at
                                    ? dayjs(interviewData.scheduled_at).format("DD-MM-YYYY")
                                    : null
                            }
                        />
                        <Field
                            label="Scheduled Time"
                            value={
                                interviewData?.scheduled_at
                                    ? dayjs(interviewData.scheduled_at).format("hh:mm A")
                                    : null
                            }
                        />
                    </Grid>

                    {/* Duration + Meeting URL */}
                    <Grid sx={{ display: "flex" }}>
                        <Field
                            label="Duration"
                            value={
                                interviewData?.duration_minutes
                                    ? `${interviewData.duration_minutes} minutes`
                                    : null
                            }
                        />
                        <Field
                            label="Meeting URL"
                            value={interviewData?.meeting_url}
                        />
                    </Grid>

                    {/* Interview Called Off */}
                    <Grid sx={{ display: "flex" }}>
                        <Field
                            label="Reason for Calling Off the Interview by HR"
                            value={
                                interviewData?.deletion_reason
                                    ? `${interviewData.deletion_reason}`
                                    : '-'
                            }
                        />
                    </Grid>

                    {/* ================= INTERVIEW STATUS ================= */}
                    <Grid sx={sectionTitleStyle}>
                        <FactCheckOutlinedIcon sx={sectionIconStyle} />
                        <Typography sx={sectionTextStyle}>
                            Interview Status
                        </Typography>
                    </Grid>

                    {/* Status + Result */}
                    <Grid
                        sx={{
                            display: "flex",
                            mt: -1,
                        }}
                    >
                        <Field
                            label="Status"
                            value={interviewData?.status}
                        />
                        <Field
                            label="Result"
                            value={interviewData?.result}
                        />
                    </Grid>

                    {/* Interviewer Response + Responded At */}
                    <Grid sx={{ display: "flex" }}>
                        <Field
                            label={`${getLabel('INTERVIEWER')} Response`}
                            value={interviewData?.interviewer_response_status}
                        />
                        <Field
                            label={`${getLabel('INTERVIEWER')} Responded At`}
                            value={
                                interviewData?.interviewer_responded_at
                                    ? dayjs(interviewData.interviewer_responded_at).format("DD-MM-YYYY hh:mm A")
                                    : null
                            }
                        />
                    </Grid>

                    {/* Rejection Reason */}
                    <Grid sx={{ display: "flex" }}>
                        <Field
                            label={`${getLabel('INTERVIEWER')} Rejection Reason`}
                            value={interviewData?.interviewer_rejection_reason}
                        />

                        {/* Remarks */}
                        <Field
                            label="Remarks"
                            value={interviewData?.remarks}
                        />
                    </Grid>

                    {/* ================= DOCUMENTS ================= */}
                    {/* <Grid sx={sectionTitleStyle}>
                        <DescriptionOutlinedIcon sx={sectionIconStyle} />
                        <Typography sx={sectionTextStyle}>
                            Documents
                        </Typography>
                    </Grid> */}

                    {/* Resume + Transcript */}
                    {/* <Grid
                        sx={{
                            display: "flex",
                            mt: -1,
                        }}
                    >
                        <Grid item xs={6} sx={halfWidth}>
                            <Typography fontWeight="600">
                                Resume
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ mt: 0.5 }}
                                href={interviewData?.meeting_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!interviewData?.meeting_url}
                            >
                                View Resume
                            </Button>
                        </Grid>
                        <Grid item xs={6} sx={halfWidth}>
                            <Typography fontWeight="600">
                                Transcript
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ mt: 0.5 }}
                                href={interviewData?.transcript_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={!interviewData?.transcript_url}
                            >
                                View Transcript
                            </Button>
                        </Grid>
                    </Grid> */}

                    {/* ================= Feedback ================= */}
                    {interviewData?.evaluation && (
                        <>
                            <Grid sx={sectionTitleStyle}>
                                <FeedbackOutlined sx={sectionIconStyle} />
                                <Typography sx={sectionTextStyle}>
                                    Interview Feedback
                                </Typography>
                            </Grid>

                            <Box
                                sx={{
                                    mt: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                }}
                            >
                                {/* Evaluation Summary */}
                                <Box
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "10px",
                                        p: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "18px",
                                            fontWeight: 600,
                                            mb: 2,
                                        }}
                                    >
                                        AI Evaluation
                                    </Typography>

                                    {/* Score Details */}
                                    <Grid
                                        sx={{
                                            display: "flex",
                                            gap: 4,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight="600">
                                                Overall Score
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: "24px",
                                                    fontWeight: "bold",
                                                    color: "#1c97e3",
                                                    mt: 0.5,
                                                }}
                                            >
                                                {interviewData?.evaluation?.overall_score ?? "0"}/100
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography fontWeight="600">
                                                Match Percentage
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: "24px",
                                                    fontWeight: "bold",
                                                    color: "#1c97e3",
                                                    mt: 0.5,
                                                }}
                                            >
                                                {interviewData?.evaluation?.match_percentage != null
                                                    ? `${interviewData.evaluation.match_percentage}%`
                                                    : "-"}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography fontWeight="600">
                                                Recommendation
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: "16px",
                                                    fontWeight: 600,
                                                    mt: 1,
                                                }}
                                            >
                                                {interviewData?.evaluation?.recommendation || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Summary */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography fontWeight="600">
                                            Summary
                                        </Typography>

                                        <Typography sx={{ mt: 0.5 }}>
                                            {interviewData?.evaluation?.summary || "-"}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Skill Scores */}
                                {interviewData?.evaluation?.skill_scores?.length > 0 && (
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: 600,
                                                mb: 1.5,
                                            }}
                                        >
                                            Skill Scores
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1.5,
                                            }}
                                        >
                                            {interviewData.evaluation.skill_scores.map(
                                                (skill, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            border: "1px solid #e0e0e0",
                                                            borderRadius: "10px",
                                                            p: 2,
                                                        }}
                                                    >
                                                        <Grid
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <Typography fontWeight="600">
                                                                {skill?.skill_name || "-"}
                                                            </Typography>

                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 0.5,
                                                                }}
                                                            >
                                                                <StarIcon
                                                                    sx={{
                                                                        color: "#f5b400",
                                                                        fontSize: "20px",
                                                                    }}
                                                                />

                                                                <Typography fontWeight="600">
                                                                    {skill?.score ?? "-"} / 10
                                                                </Typography>
                                                            </Box>
                                                        </Grid>

                                                        <Typography
                                                            sx={{
                                                                mt: 0.5,
                                                                fontSize: "14px",
                                                                color: "#555",
                                                            }}
                                                        >
                                                            {skill?.feedback || "-"}
                                                        </Typography>
                                                    </Box>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Strengths */}
                                {interviewData?.evaluation?.strengths?.length > 0 && (
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: 600,
                                                mb: 1.5,
                                            }}
                                        >
                                            Strengths
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            }}
                                        >
                                            {interviewData.evaluation.strengths.map(
                                                (strength, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <CheckCircleOutlineIcon
                                                            sx={{
                                                                color: "#22a06b",
                                                                fontSize: "20px",
                                                                mt: 0.2,
                                                            }}
                                                        />

                                                        <Typography>
                                                            {strength}
                                                        </Typography>
                                                    </Box>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Weaknesses */}
                                {interviewData?.evaluation?.weaknesses?.length > 0 && (
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: 600,
                                                mb: 1.5,
                                            }}
                                        >
                                            Areas for Improvement
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            }}
                                        >
                                            {interviewData.evaluation.weaknesses.map(
                                                (weakness, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <WarningAmberIcon
                                                            sx={{
                                                                color: "#f59e0b",
                                                                fontSize: "20px",
                                                                mt: 0.2,
                                                            }}
                                                        />

                                                        <Typography>
                                                            {weakness}
                                                        </Typography>
                                                    </Box>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {/* Evidence */}
                                {interviewData?.evaluation?.evidence?.length > 0 && (
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: "18px",
                                                fontWeight: 600,
                                                mb: 1.5,
                                            }}
                                        >
                                            Evidence
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 2,
                                            }}
                                        >
                                            {interviewData.evaluation.evidence.map(
                                                (evidence, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            border: "1px solid #e0e0e0",
                                                            borderRadius: "10px",
                                                            p: 2,
                                                        }}
                                                    >
                                                        <Grid
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    textTransform: "capitalize",
                                                                }}
                                                            >
                                                                {evidence?.type || "-"}
                                                            </Typography>

                                                            <Typography
                                                                sx={{
                                                                    fontSize: "13px",
                                                                    color: "#666",
                                                                }}
                                                            >
                                                                {evidence?.source || "-"}
                                                            </Typography>
                                                        </Grid>

                                                        {evidence?.weakness && (
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                {evidence.weakness}
                                                            </Typography>
                                                        )}

                                                        {evidence?.quote && (
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    gap: 1,
                                                                    backgroundColor: "#f7f7f7",
                                                                    borderRadius: "8px",
                                                                    p: 1.5,
                                                                }}
                                                            >
                                                                <FormatQuoteIcon
                                                                    sx={{
                                                                        color: "#1c97e3",
                                                                        fontSize: "20px",
                                                                    }}
                                                                />

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: "14px",
                                                                        fontStyle: "italic",
                                                                        color: "#555",
                                                                    }}
                                                                >
                                                                    {evidence.quote}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ViewInterviewScheduler;